import { useMemo, useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { useAssignWorkoutToCycleDialog } from '../../hooks/dialogs/useAssignWorkoutToCycleDialog';
import WeekGridAssign, { WeekGridAssignment } from '../shared/WeekGridAssign';

interface ICycleOption {
  label: string;
  value: number;
  weeks?: number;
}

interface IAssignWorkoutToCycleDialogProps {
  visible: boolean;
  onHide: () => void;
  clientId: number | string;
  setRefreshKey: (fn: (old: number) => number) => void;
  cycleOptions: ICycleOption[];
  actionType: 'assign' | 'unassign';
}

/**
 * v2 — Asignar / Desasignar entrenamientos a un ciclo.
 *
 * Cambios respecto al legacy:
 * - Rows numeradas con número grande a la izquierda (no Card wrapper, hairline divisor).
 * - Week preview: una fila de dots indica en qué semanas del ciclo va la asignación.
 * - Footer estilo v2: text-button izquierda (Agregar / Cancelar), lime CTA derecha.
 * - Estilos compartidos con `et-cycle-wizard__chip` para coherencia con el wizard.
 *
 * Design handoff §3 (mapeo): "Visual: rows numeradas + week preview".
 */
const AssignWorkoutToCycleDialog = ({
  visible,
  onHide,
  clientId,
  setRefreshKey,
  cycleOptions,
  actionType
}: IAssignWorkoutToCycleDialogProps) => {
  const {
    intl,
    workouts,
    assignments,
    cycle,
    cycles,
    loading,
    selectedDay,
    assignedWorkouts,
    unassignOption,
    verificationResult,
    forceDelete,
    showVerificationDialog,
    daysOfWeek,
    setUnassignOption,
    setForceDelete,
    setShowVerificationDialog,
    handleAction,
    handleAssignmentChange,
    setAssignmentForDay,
    clearAssignmentForDay,
    existingByWeek,
    handleCycleChange,
    handleDayChange,
    replicate,
    setReplicate
  } = useAssignWorkoutToCycleDialog({
    visible,
    onHide,
    clientId,
    setRefreshKey,
    cycleOptions,
    actionType
  });

  const selectedCycle = cycles.find((c) => c.value === cycle);
  const weeksInCycle = selectedCycle?.weeks ?? 0;

  const [selectedWeek, setSelectedWeek] = useState(1);
  useEffect(() => {
    if (visible) setSelectedWeek(1);
  }, [visible, cycle]);

  const gridAssignments: WeekGridAssignment[] = useMemo(
    () =>
      assignments
        .filter((a) => a.workoutId != null && a.dayOfWeek != null)
        .map((a) => ({ workoutId: a.workoutId as number, dayOfWeek: a.dayOfWeek as number })),
    [assignments]
  );

  const lockedForCurrentWeek: WeekGridAssignment[] = useMemo(
    () => existingByWeek?.[selectedWeek] ?? [],
    [existingByWeek, selectedWeek]
  );

  /** Render N small week dots showing where the assignment will land. */
  const renderWeekPreview = (dayNumber: number | null) => {
    if (weeksInCycle <= 0 || dayNumber == null) return null;
    return (
      <div className="et-assign__week-preview" title={`Esta asignación va en las ${weeksInCycle} semanas del ciclo`}>
        <span className="et-assign__week-label">
          {intl.formatMessage(
            { id: 'assignWorkoutDialog.weekPreview', defaultMessage: 'En las {n} semanas:' },
            { n: weeksInCycle }
          )}
        </span>
        <div className="et-assign__week-dots">
          {Array.from({ length: weeksInCycle }).map((_, i) => (
            <span key={i} className="et-assign__week-dot" aria-hidden />
          ))}
        </div>
      </div>
    );
  };

  const renderVerificationDialog = () => {
    if (!verificationResult) return null;

    const hasSetLogs = verificationResult.hasSetLogs;
    const setLogsCount = verificationResult.setLogsCount;
    const canDelete = !hasSetLogs || forceDelete;

    return (
      <Dialog
        header={intl.formatMessage({ id: 'assignWorkoutToCycleDialog.verificationTitle' })}
        visible={showVerificationDialog}
        style={{ width: 'min(560px, 95vw)' }}
        onHide={() => setShowVerificationDialog(false)}
        className="verification-dialog"
        footer={
          <div className="et-assign__footer">
            <Button
              label={intl.formatMessage({ id: 'assignWorkoutToCycleDialog.cancel' })}
              icon="pi pi-times"
              onClick={() => setShowVerificationDialog(false)}
              className="p-button-text"
            />
            <Button
              label={intl.formatMessage({ id: 'assignWorkoutToCycleDialog.confirm' })}
              icon="pi pi-check"
              onClick={() => handleAction()}
              className="p-button-danger"
              disabled={!canDelete}
            />
          </div>
        }
      >
        <div className="p-fluid">
          {hasSetLogs ? (
            <>
              <div className="flex align-items-center gap-2 mb-3">
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ color: 'var(--warning-color)', fontSize: '1.5rem' }}
                />
                <h3 className="m-0">{intl.formatMessage({ id: 'assignWorkoutToCycleDialog.warningTitle' })}</h3>
              </div>
              <p className="mb-3">
                {intl.formatMessage({ id: 'assignWorkoutToCycleDialog.warningMessage' }, { count: setLogsCount })}
              </p>
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="forceDelete"
                  checked={forceDelete}
                  onChange={(e: CheckboxChangeEvent) => setForceDelete(!!e.checked)}
                />
                <label htmlFor="forceDelete">
                  {intl.formatMessage({ id: 'assignWorkoutToCycleDialog.forceDelete' })}
                </label>
              </div>
            </>
          ) : (
            <div className="flex align-items-center gap-2">
              <i className="pi pi-check-circle" style={{ color: 'var(--success-color)', fontSize: '1.5rem' }} />
              <p className="m-0">{intl.formatMessage({ id: 'assignWorkoutToCycleDialog.safeToDelete' })}</p>
            </div>
          )}
        </div>
      </Dialog>
    );
  };

  return (
    <>
      <Dialog
        draggable={false}
        dismissableMask
        resizable={false}
        header={
          actionType === 'assign'
            ? intl.formatMessage({ id: 'assignWorkoutToCycleDialog.assignWorkoutsToCycle' })
            : intl.formatMessage({ id: 'assignWorkoutToCycleDialog.unassignWorkoutsFromCycle' })
        }
        className="responsive-dialog"
        visible={visible}
        style={{ width: actionType === 'assign' ? 'min(1140px, 96vw)' : 'min(720px, 95vw)' }}
        onHide={onHide}
        footer={
          <div className="et-assign__footer">
            <Button
              label={intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancelar' })}
              onClick={onHide}
              className="p-button-text"
            />
            <Button
              label={
                actionType === 'assign'
                  ? intl.formatMessage({ id: 'assignWorkoutToCycleDialog.assignWorkouts' })
                  : unassignOption === 'day'
                    ? intl.formatMessage({ id: 'assignWorkoutToCycleDialog.unassignWorkouts' })
                    : intl.formatMessage({ id: 'assignWorkoutToCycleDialog.deleteCycle' })
              }
              icon={actionType === 'assign' ? 'pi pi-check' : 'pi pi-trash'}
              onClick={() => handleAction(selectedWeek)}
              loading={loading}
              disabled={cycle === -1}
              style={
                actionType === 'assign'
                  ? {
                      background: 'var(--et-cta-bg)',
                      borderColor: 'var(--et-cta-bg)',
                      color: 'var(--et-cta-fg)'
                    }
                  : undefined
              }
              className={actionType === 'unassign' ? 'p-button-danger' : ''}
            />
          </div>
        }
      >
        <div className="et-assign">
          <div className="et-assign__field">
            <label className="et-assign__field-label">
              {intl.formatMessage({ id: 'assignWorkoutToCycleDialog.cycle' })}
            </label>
            <Dropdown
              value={cycle}
              options={cycles.map((c) => ({ label: c.label, value: c.value })).filter((c) => c.value !== -1)}
              onChange={(e) => handleCycleChange(e.value)}
              placeholder={intl.formatMessage({ id: 'assignWorkoutToCycleDialog.selectCycle' })}
              className="w-full"
            />
            {weeksInCycle > 0 && (
              <small className="et-assign__cycle-hint">
                {intl.formatMessage(
                  { id: 'assignWorkoutDialog.cycleWeeksHint', defaultMessage: 'Ciclo con {n} semanas' },
                  { n: weeksInCycle }
                )}
              </small>
            )}
          </div>

          {actionType === 'unassign' && cycle !== -1 && (
            <div className="et-assign__mode-toggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={unassignOption === 'day'}
                onClick={() => setUnassignOption('day')}
                className={`et-assign__mode-chip${unassignOption === 'day' ? ' is-active' : ''}`}
              >
                {intl.formatMessage({ id: 'assignWorkoutToCycleDialog.unassignByDay' })}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={unassignOption === 'cycle'}
                onClick={() => setUnassignOption('cycle')}
                className={`et-assign__mode-chip${unassignOption === 'cycle' ? ' is-active' : ''}`}
              >
                {intl.formatMessage({ id: 'assignWorkoutToCycleDialog.deleteEntireCycle' })}
              </button>
            </div>
          )}

          {actionType === 'unassign' && cycle !== -1 && unassignOption === 'day' && (
            <div className="et-assign__unassign-row">
              <div className="et-assign__field">
                <label className="et-assign__field-label">
                  {intl.formatMessage({ id: 'assignWorkoutToCycleDialog.selectDayOfWeek' })}
                </label>
                <Dropdown
                  value={selectedDay}
                  options={daysOfWeek}
                  onChange={(e) => handleDayChange(e.value)}
                  placeholder={intl.formatMessage({ id: 'assignWorkoutToCycleDialog.selectDayOfWeek' })}
                  className="w-full"
                />
              </div>
              <div className="et-assign__field">
                <label className="et-assign__field-label">
                  {intl.formatMessage({
                    id: 'assignWorkoutToCycleDialog.unassignWorkouts',
                    defaultMessage: 'Sesión a quitar'
                  })}
                </label>
                <Dropdown
                  value={assignments[0]?.workoutId}
                  options={assignedWorkouts.map((w) => ({ label: w.label, value: w.value }))}
                  onChange={(e) => handleAssignmentChange(0, 'workoutId', e.value)}
                  placeholder={intl.formatMessage({ id: 'assignWorkoutToCycleDialog.selectWorkoutToUnassign' })}
                  disabled={selectedDay === null}
                  className="w-full"
                />
              </div>
              {renderWeekPreview(selectedDay)}
            </div>
          )}

          {actionType === 'assign' && cycle !== -1 && (
            <div className="et-assign__week-grid-wrap">
              <WeekGridAssign
                workouts={workouts}
                durationInWeeks={weeksInCycle || 1}
                startDate={null}
                assignments={gridAssignments}
                lockedAssignments={lockedForCurrentWeek}
                onAssign={setAssignmentForDay}
                onUnassign={clearAssignmentForDay}
                selectedWeek={selectedWeek}
                onWeekChange={setSelectedWeek}
                replicate={replicate}
                onReplicateChange={setReplicate}
              />
            </div>
          )}
        </div>
      </Dialog>
      {renderVerificationDialog()}
    </>
  );
};

export default AssignWorkoutToCycleDialog;
